import { useState, useEffect } from 'react';
import { supabaseClient } from '../../lib/supabase';
import { LoadingScreen } from '../common/LoadingScreen';
import { toast } from '../../lib/toast';
import { RefreshCw, Play, Pause, AlertTriangle, Check, X, Clock, ArrowUp, ArrowDown, Filter, Trash2, BarChart2 } from 'lucide-react';
import { processQueue, removeFromQueue, retryFailedItem, changePriority } from '../../lib/analysis-queue';
import { useNavigate } from 'react-router-dom';

interface QueueItem {
  id: string;
  analysis_id: string;
  priority: number;
  status: 'queued' | 'processing' | 'completed' | 'failed';
  attempts: number;
  last_attempt_at: string | null;
  created_at: string;
  updated_at: string;
  error_message: string | null;
  analysis: {
    id: string;
    name: string;
    keyword: string;
    project: {
      id: string;
      name: string;
    };
  };
}

interface ProjectOption {
  id: string;
  name: string;
}

interface QueueStats {
  total: number;
  queued: number;
  processing: number;
  completed: number;
  failed: number;
}

export function AnalysisQueuePage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [queueItems, setQueueItems] = useState<QueueItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [projectFilter, setProjectFilter] = useState<string>('all');
  const [projects, setProjects] = useState<ProjectOption[]>([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [stats, setStats] = useState<QueueStats>({
    total: 0,
    queued: 0,
    processing: 0,
    completed: 0,
    failed: 0
  });
  const [autoRefresh, setAutoRefresh] = useState(false);
  const [showErrorDetails, setShowErrorDetails] = useState<string | null>(null);

  // Set up auto-refresh
  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    
    if (autoRefresh) {
      interval = setInterval(() => {
        setRefreshKey(prev => prev + 1);
      }, 10000); // Refresh every 10 seconds
    }
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [autoRefresh]);

  // Load queue items and projects
  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Load projects for filtering
        const { data: projectsData, error: projectsError } = await supabaseClient
          .from('projects')
          .select('id, name')
          .order('name');

        if (projectsError) throw projectsError;
        setProjects(projectsData || []);

        // Build queue query
        let query = supabaseClient
          .from('analysis_queue')
          .select(`
            *,
            analysis:analyses (
              id,
              name,
              keyword,
              project:projects (
                id,
                name
              )
            )
          `)
          .order('priority', { ascending: false })
          .order('created_at', { ascending: true });

        // Apply filters
        if (statusFilter !== 'all') {
          query = query.eq('status', statusFilter);
        }

        const { data, error: queueError } = await query;

        if (queueError) throw queueError;
        
        // Apply project filter in memory (since we need to filter on a nested property)
        let filteredItems = data || [];
        if (projectFilter !== 'all') {
          filteredItems = filteredItems.filter(item => 
            item.analysis?.project?.id === projectFilter
          );
        }
        
        setQueueItems(filteredItems);

        // Calculate stats
        const allItems = data || [];
        setStats({
          total: allItems.length,
          queued: allItems.filter(item => item.status === 'queued').length,
          processing: allItems.filter(item => item.status === 'processing').length,
          completed: allItems.filter(item => item.status === 'completed').length,
          failed: allItems.filter(item => item.status === 'failed').length
        });
      } catch (err) {
        console.error('Error loading data:', err);
        setError(err instanceof Error ? err.message : 'Failed to load data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [statusFilter, projectFilter, refreshKey]);

  const handleRefresh = () => {
    setRefreshKey(prev => prev + 1);
  };

  const handleProcessQueue = async () => {
    try {
      setIsProcessing(true);
      setError(null);

      const result = await processQueue();
      
      if (result.processed > 0) {
        toast.success(`Processed ${result.success} of ${result.processed} items successfully`);
      } else {
        toast.success('No items in queue to process');
      }
      
      handleRefresh();
    } catch (err) {
      console.error('Error processing queue:', err);
      setError(err instanceof Error ? err.message : 'Failed to process queue');
      toast.error('Failed to process queue');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleRetryItem = async (id: string) => {
    try {
      await retryFailedItem(id);
      toast.success('Item queued for retry');
      handleRefresh();
    } catch (err) {
      console.error('Error retrying item:', err);
      toast.error('Failed to retry item');
    }
  };

  const handleResetStuckItem = async (id: string) => {
    try {
      const { error } = await supabaseClient
        .from('analysis_queue')
        .update({
          status: 'queued',
          error_message: 'Manually reset from stuck state',
          updated_at: new Date().toISOString()
        })
        .eq('id', id);

      if (error) throw error;
      
      toast.success('Item reset to queued state');
      handleRefresh();
    } catch (err) {
      console.error('Error resetting item:', err);
      toast.error('Failed to reset item');
    }
  };

  const handleRemoveItem = async (id: string) => {
    try {
      const confirmed = window.confirm('Are you sure you want to remove this item from the queue?');
      if (!confirmed) return;
      
      const result = await removeFromQueue(id);
      
      if (result) {
        toast.success('Item removed from queue');
        handleRefresh();
      }
    } catch (err) {
      console.error('Error removing item:', err);
      toast.error('Failed to remove from queue');
    }
  };

  const handleChangePriority = async (id: string, direction: 'up' | 'down') => {
    try {
      const item = queueItems.find(item => item.id === id);
      if (!item) return;

      const newPriority = direction === 'up' ? item.priority + 1 : Math.max(1, item.priority - 1);
      
      const result = await changePriority(id, newPriority);
      
      if (result) {
        toast.success(`Priority ${direction === 'up' ? 'increased' : 'decreased'}`);
        handleRefresh();
      }
    } catch (err) {
      console.error('Error changing priority:', err);
      toast.error('Failed to change priority');
    }
  };

  const getStatusBadgeColor = (status: string): string => {
    switch (status) {
      case 'queued':
        return 'bg-blue-100 text-blue-800';
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'queued':
        return <Clock className="h-4 w-4" />;
      case 'processing':
        return <RefreshCw className="h-4 w-4 animate-spin" />;
      case 'completed':
        return <Check className="h-4 w-4" />;
      case 'failed':
        return <X className="h-4 w-4" />;
      default:
        return null;
    }
  };

  // Check for stuck items (processing for more than 10 minutes)
  const isItemStuck = (item: QueueItem) => {
    if (item.status !== 'processing' || !item.last_attempt_at) return false;
    
    const lastAttempt = new Date(item.last_attempt_at).getTime();
    const tenMinutesAgo = Date.now() - 10 * 60 * 1000;
    
    return lastAttempt < tenMinutesAgo;
  };

  // Reset all stuck items
  const handleResetAllStuckItems = async () => {
    try {
      const stuckItems = queueItems.filter(isItemStuck);
      if (stuckItems.length === 0) {
        toast.info('No stuck items to reset');
        return;
      }

      const confirmed = window.confirm(`Are you sure you want to reset ${stuckItems.length} stuck items?`);
      if (!confirmed) return;

      let successCount = 0;
      for (const item of stuckItems) {
        try {
          const { error } = await supabaseClient
            .from('analysis_queue')
            .update({
              status: 'queued',
              error_message: 'Manually reset from stuck state (batch reset)',
              updated_at: new Date().toISOString()
            })
            .eq('id', item.id);

          if (error) throw error;
          successCount++;
        } catch (itemError) {
          console.error(`Error resetting item ${item.id}:`, itemError);
        }
      }

      toast.success(`Reset ${successCount} of ${stuckItems.length} stuck items`);
      handleRefresh();
    } catch (err) {
      console.error('Error resetting stuck items:', err);
      toast.error('Failed to reset stuck items');
    }
  };

  if (loading) return <LoadingScreen />;

  // Count stuck items
  const stuckItemsCount = queueItems.filter(isItemStuck).length;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-xl font-semibold text-gray-800">Analysis Queue</h2>
        <div className="flex gap-2">
          <button
            onClick={() => setAutoRefresh(!autoRefresh)}
            className={`inline-flex items-center px-4 py-2 border rounded-md shadow-sm text-sm font-medium ${
              autoRefresh 
                ? 'border-green-500 text-green-700 bg-green-50 hover:bg-green-100' 
                : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
            }`}
          >
            {autoRefresh ? (
              <>
                <Pause className="h-4 w-4 mr-2" />
                Auto-refresh On
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Auto-refresh Off
              </>
            )}
          </button>
          <button
            onClick={handleRefresh}
            className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </button>
          {stuckItemsCount > 0 && (
            <button
              onClick={handleResetAllStuckItems}
              className="inline-flex items-center px-4 py-2 border border-orange-300 rounded-md shadow-sm text-sm font-medium text-orange-700 bg-orange-50 hover:bg-orange-100"
            >
              <AlertTriangle className="h-4 w-4 mr-2" />
              Reset {stuckItemsCount} Stuck
            </button>
          )}
          <button
            onClick={handleProcessQueue}
            disabled={isProcessing}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {isProcessing ? (
              <>
                <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                Processing...
              </>
            ) : (
              <>
                <Play className="h-4 w-4 mr-2" />
                Process Queue
              </>
            )}
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 p-4 text-sm text-red-700 bg-red-100 rounded-md">
          {error}
        </div>
      )}

      <div className="mb-6 grid grid-cols-1 md:grid-cols-5 gap-4">
        <div className="bg-white p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Total</h3>
          <p className="text-2xl font-bold text-gray-700">{stats.total}</p>
        </div>
        <div className="bg-blue-50 p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-blue-900 mb-2">Queued</h3>
          <p className="text-2xl font-bold text-blue-700">{stats.queued}</p>
        </div>
        <div className="bg-yellow-50 p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-yellow-900 mb-2">Processing</h3>
          <p className="text-2xl font-bold text-yellow-700">{stats.processing}</p>
          {stuckItemsCount > 0 && (
            <p className="text-sm text-orange-600 mt-1">
              {stuckItemsCount} stuck
            </p>
          )}
        </div>
        <div className="bg-green-50 p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-green-900 mb-2">Completed</h3>
          <p className="text-2xl font-bold text-green-700">{stats.completed}</p>
        </div>
        <div className="bg-red-50 p-4 rounded-lg shadow">
          <h3 className="text-sm font-medium text-red-900 mb-2">Failed</h3>
          <p className="text-2xl font-bold text-red-700">{stats.failed}</p>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-4">
        <div>
          <label htmlFor="status-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Status
          </label>
          <select
            id="status-filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="block w-48 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="all">All Statuses</option>
            <option value="queued">Queued</option>
            <option value="processing">Processing</option>
            <option value="completed">Completed</option>
            <option value="failed">Failed</option>
          </select>
        </div>
        <div>
          <label htmlFor="project-filter" className="block text-sm font-medium text-gray-700 mb-1">
            Project
          </label>
          <select
            id="project-filter"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
            className="block w-64 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
          >
            <option value="all">All Projects</option>
            {projects.map(project => (
              <option key={project.id} value={project.id}>{project.name}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="overflow-x-auto bg-white rounded-lg shadow">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Priority
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Analysis
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Project
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Attempts
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Last Attempt
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {queueItems.length > 0 ? (
              queueItems.map((item) => (
                <tr key={item.id} className={`${item.status === 'failed' ? 'bg-red-50' : ''} ${isItemStuck(item) ? 'bg-orange-50' : ''}`}>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(item.status)}`}>
                      <span className="mr-1">{getStatusIcon(item.status)}</span>
                      {item.status}
                      {isItemStuck(item) && (
                        <span className="ml-1 text-orange-600">(stuck)</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <span className="text-sm text-gray-900 mr-2">{item.priority}</span>
                      {item.status === 'queued' && (
                        <div className="flex flex-col">
                          <button
                            onClick={() => handleChangePriority(item.id, 'up')}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <ArrowUp className="h-3 w-3" />
                          </button>
                          <button
                            onClick={() => handleChangePriority(item.id, 'down')}
                            className="text-gray-400 hover:text-gray-600"
                            disabled={item.priority <= 1}
                          >
                            <ArrowDown className="h-3 w-3" />
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">
                      {item.analysis?.name || 'Unknown Analysis'}
                    </div>
                    <div className="text-sm text-gray-500">
                      {item.analysis?.keyword || 'No keyword'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {item.analysis?.project?.name || 'Unknown Project'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.attempts}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.last_attempt_at ? new Date(item.last_attempt_at).toLocaleString() : 'Never'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      {item.status === 'failed' && (
                        <button
                          onClick={() => handleRetryItem(item.id)}
                          className="text-indigo-600 hover:text-indigo-900"
                        >
                          Retry
                        </button>
                      )}
                      {isItemStuck(item) && (
                        <button
                          onClick={() => handleResetStuckItem(item.id)}
                          className="text-orange-600 hover:text-orange-900"
                        >
                          Reset
                        </button>
                      )}
                      <button
                        onClick={() => handleRemoveItem(item.id)}
                        className="text-red-600 hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                      {item.error_message && (
                        <button
                          onClick={() => setShowErrorDetails(showErrorDetails === item.id ? null : item.id)}
                          className="text-yellow-600 hover:text-yellow-900"
                        >
                          <AlertTriangle className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                    {showErrorDetails === item.id && item.error_message && (
                      <div className="mt-2 p-2 text-xs text-left text-red-600 bg-red-50 rounded border border-red-200">
                        {item.error_message}
                      </div>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={7} className="px-6 py-4 text-center text-sm text-gray-500">
                  No queue items found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}